import { LoadingOutlined, UserOutlined } from '@ant-design/icons';
import { useOidc } from '@axa-fr/react-oidc';
import { useHotkeyManager, useIsMutating, useTranslation } from '@threatadvisor/react-utils';
import { Layout, Menu, Switch } from 'antd';
import { createStyles } from 'antd-style';
import React, { ReactElement, ReactNode, Suspense, useEffect, useState } from 'react';
import { BsMoon, BsSun } from 'react-icons/bs';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AppNameBanner from '../AppNameBanner/AppNameBanner';
import { useTheme } from '../ThemeProvider';
import { useUserContext } from '../oidc/UserProvider';

export type MenuItem = {
  key: string;
  component?: ReactElement;
  titleKey?: string;
  title?: string;
  dividerAfter?: boolean;
  hotkey?: string;
  route?: string;
  hide?: boolean;
  icon?: ReactElement;
  children?: MenuItem[];
  //renderChildRoutes?: (children: ReactNode) => ReactElement;
  extraContent?: { top?: ReactNode; bottom?: ReactNode };
};

export type TAMenuProps = {
  menuItems: MenuItem[];
  title: string;
  rightExtra?: ReactNode;
  topMenuComponent?: ReactElement;
  oidcUrl?: string;
  siderOnly?: boolean;
};

const useNewStyles = createStyles((props: { isDarkMode: boolean }) => {
  return {
    menuStyle: {
      backgroundColor: props.isDarkMode ? '#1f1f1f' : '#fff',
      boxShadow: props.isDarkMode
        ? '-6px 0 5px 0px rgba(0, 0, 0, 0.4)'
        : '-6px 0 5px 0px rgba(100, 100, 100, 0.2)',
      position: 'sticky',
      display: 'flex',
      paddingLeft: 'unset',
      'ant-table-footer': {
        // Adjust table footer to make it same height as sidebar menu collapse button
        paddingTop: '13px',
        paddingBottom: '13px'
      }
    }
  };
});

export default function TAMenu(props: TAMenuProps): ReactElement {
  const { menuItems, title, rightExtra, topMenuComponent, oidcUrl, siderOnly } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<Array<string>>([location.pathname.substring(1)]);
  const { isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const isMutating = useIsMutating();

  const { cx, styles } = useNewStyles();

  const { logout } = useOidc('config_show_access_token');
  const { oidcUser } = useUserContext();

  const fullName = oidcUser ? oidcUser.email : '';

  useEffect(() => {
    setSelectedKeys([location.pathname.substring(1)]);
  }, [location.pathname]);

  // This enables using hotkeys such as gp to go the project page from anywhere in the app
  const hotkeyManager = useHotkeyManager();
  useEffect(() => {
    menuItems.forEach(item => {
      item.children &&
        item.children.forEach(child => {
          if (child.hotkey) {
            hotkeyManager.registerNavigateRoute(child.hotkey, `/${item.key}/${child.key}`);
          }
        });
    });
  }, [menuItems, hotkeyManager]);

  function handleMenuClick(key: string) {
    // If select top-level menu item, automatically select first child
    if (!key.includes('/')) {
      const parentItem = menuItems.find(item => item.key === key);
      if (parentItem && parentItem.children && parentItem.children?.length > 0) {
        navigate('/' + key + '/' + parentItem.children[0].key);
        return;
      }
    }
    navigate('/' + key);
  }

  const onLogoutClick = () => {
    logout(`${oidcUrl}/connect/logout`);
  };

  const selectedMenuItem = menuItems.find(({ key }) => location.pathname.split('/')[1] === key);

  function cyTestName(menuItem: MenuItem) {
    return `menu-${(menuItem.title ?? t('labels.' + (menuItem.titleKey ?? menuItem.key))).replaceAll(' ', '-')}`;
  }

  return (
    <Layout
      style={{
        overflowX: 'hidden',
        position: 'absolute',
        right: 0,
        left: 0,
        top: 0,
        bottom: 0,
        height: '100%'
        // Hide (but don't unmount) while theme loading so they don't see
        // weird visual glitches with changing CSS
        //display: themeLoaded ? undefined : 'none'
      }}
    >
      <Layout.Header className={cx(styles.menuStyle)}>
        <AppNameBanner title={title} portalUrl={oidcUrl ?? '/'} />
        {topMenuComponent ? (
          <>{topMenuComponent}</>
        ) : (
          <Menu
            // theme={currentTheme as 'light' | 'dark'}
            mode="horizontal"
            selectedKeys={selectedKeys.map(key => key.split('/')[0])}
            style={{ flex: '1 1 auto', alignItems: 'center' }}
            onSelect={
              siderOnly
                ? () => {}
                : ({ key }) => (typeof key === 'string' ? handleMenuClick(key) : null)
            }
            items={
              siderOnly
                ? []
                : menuItems.map(({ key, titleKey, icon }) => ({
                    key,
                    icon,
                    label: (
                      <span data-test={`${titleKey}-nav`}>{t('labels.' + (titleKey ?? key))}</span>
                    )
                  }))
            }
          />
        )}

        {rightExtra}
        <Menu
          // theme={currentTheme as 'light' | 'dark'}
          disabledOverflow={true}
          mode="horizontal"
          items={[
            {
              key: 'user-options',
              icon: <UserOutlined />,
              title: fullName,
              children: [
                {
                  key: 'account',
                  label: t('labels.account', 'Account'),
                  onClick: () => {
                    window.location.href = '/account';
                  }
                },
                {
                  key: 'log-out',
                  label: t('labels.logOut', 'Log Out'),
                  onClick: onLogoutClick
                }
              ]
            }
          ]}
        />
        <Switch
          checkedChildren={<BsSun />}
          unCheckedChildren={<BsMoon />}
          style={{ marginTop: 22, marginLeft: 20 }}
          checked={!isDarkMode}
          onChange={() => toggleTheme()}
        />
        <div style={{ width: 20, paddingLeft: 10 }}>{isMutating > 0 && <LoadingOutlined />}</div>
      </Layout.Header>
      <Layout>
        <Layout.Sider
          collapsible
          style={{ marginTop: '4px' }}
          //theme={currentTheme as 'light' | 'dark'}
          onCollapse={collapsed => setSidebarCollapsed(collapsed)}
        >
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            //theme={currentTheme as 'light' | 'dark'}
            onSelect={({ key }) => (typeof key === 'string' ? handleMenuClick(key) : null)}
            style={{ marginTop: 10 }}
            items={
              siderOnly
                ? menuItems.map(({ key, titleKey, icon }) => ({
                    key,
                    icon,
                    label: (
                      <span data-test={`${titleKey}-nav`}>{t('labels.' + (titleKey ?? key))}</span>
                    )
                  }))
                : [
                    ...(!sidebarCollapsed ? [selectedMenuItem?.extraContent?.top] : []),
                    ...(selectedMenuItem?.children ?? [])
                      .filter(m => !m.hide)
                      .map(submenu1 =>
                        submenu1.children && submenu1.children.length > 0
                          ? [
                              {
                                'cy-test': cyTestName(submenu1),
                                key: `${selectedMenuItem!.key}/${submenu1.key}`,
                                label:
                                  submenu1.title ??
                                  t('labels.' + (submenu1.titleKey ?? submenu1.key)),
                                children: submenu1.children
                                  ?.map(submenu2 => [
                                    {
                                      key: `${selectedMenuItem!.key}/${submenu1.route ? submenu1.route + '/' : ''}${submenu2.key}`,
                                      label: submenu2.title ?? (
                                        <span data-test={`${submenu2.key}-nav`}>
                                          {t('labels.' + (submenu2.titleKey ?? submenu2.key))}
                                        </span>
                                      )
                                    },
                                    ...(submenu2.dividerAfter
                                      ? [{ key: `divider-${submenu2.key}`, type: 'divider' }]
                                      : [])
                                  ])
                                  .reduce((a: any[], b: any[]) => a.concat(b), [])
                              }
                            ]
                          : [
                              {
                                'cy-test': cyTestName(submenu1),
                                key: `${selectedMenuItem!.key}/${submenu1.key}`,
                                label: submenu1.title ?? (
                                  <span data-test={`${submenu1.key}-nav`}>
                                    {t('labels.' + (submenu1.titleKey ?? submenu1.key))}
                                  </span>
                                )
                              },
                              ...(submenu1.dividerAfter
                                ? [{ key: `divider-${submenu1.key}`, type: 'divider' }]
                                : [])
                            ]
                      )
                      .reduce((a: any[], b: any[]) => a.concat(b), []),
                    ...(!sidebarCollapsed ? [selectedMenuItem?.extraContent?.bottom] : [])
                  ]
            }
          ></Menu>
        </Layout.Sider>
        <Layout.Content style={{ display: 'flex', flexDirection: 'column' }}>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
